import React, { useState, useRef, useCallback } from 'react';
import { cn } from '../../lib/utils';
import { TITLE_FONT_SIZE_LEVELS, DEFAULT_TITLE_FONT_SIZE, BODY_FONT_SIZE_LEVELS, DEFAULT_BODY_FONT_SIZE } from '../../constants/typography';
import { MiniStat, ProgressBar, Callout, ImageWidget, VideoWidget } from './Widgets';
import { renderFormattedText } from '../../utils/formatText';
import { usePreviewInteraction } from '../../contexts/PreviewInteractionContext';
import { InlineEditable } from './InlineEditable';
import { getEffectiveContentOrder } from '../../constants/contentOrder';
import { scrollEditorToSection } from '../../utils/scrollEditor';

/**
 * Shared slide content renderer — used by ALL template layouts.
 * Renders: kicker, title, subtitle, body, widgets, bullets, stats, paragraphs, tags, footer.
 *
 * RENDER ORDER (Phase 3 — Session 48):
 *   Header (kicker + title + afterTitle) → ALWAYS FIRST
 *   Middle elements → ORDER determined by slide.contentOrder array
 *   Footer (beforeFooter + CTA) → ALWAYS LAST
 *
 * REORDER (Session 48):
 *   In interactive mode, each middle element shows a grip handle on hover.
 *   Dragging the grip handle reorders elements directly in the preview.
 */
export function SlideContent({ data, fontPreset, pc, options = {} }) {
  const {
    showKicker = true,
    showFooter = true,
    titleClass = '',
    bodyClass = '',
    footerClass = '',
    beforeFooter = null,
    afterTitle = null,
  } = options;

  const { interactive, onElementClick, editingField, onEditChange, onEndEdit, scale, onReorder, contentOrder: ctxContentOrder } = usePreviewInteraction();

  // ── Reorder state ──
  const [draggingId, setDraggingId] = useState(null);
  const [dropIndex, setDropIndex] = useState(-1);
  const elementRefsMap = useRef({});
  const dragStartY = useRef(0);
  const dragStartOrder = useRef(null);

  // Click handler factory
  const handleClick = (sectionId, fieldName) => (e) => {
    if (!interactive || !onElementClick) return;
    if (editingField === fieldName) return;
    if (draggingId) return; // Don't click during reorder
    e.stopPropagation();
    onElementClick(sectionId, fieldName);
  };

  // ── Reorder drag logic ──
  const handleGripDown = useCallback((elementId, e) => {
    e.preventDefault();
    e.stopPropagation();

    const order = getEffectiveContentOrder(data.contentOrder);
    setDraggingId(elementId);
    dragStartY.current = e.clientY;
    dragStartOrder.current = order;

    // Scroll editor panel to Content Order section (safe — only scrolls editor container)
    requestAnimationFrame(() => {
      scrollEditorToSection('content-order');
    });

    const handleMove = (moveEvent) => {
      moveEvent.preventDefault();
      const currentOrder = dragStartOrder.current;
      const currentIndex = currentOrder.indexOf(elementId);
      if (currentIndex === -1) return;

      const myCenterY = moveEvent.clientY;
      let targetIndex = currentIndex;

      for (let i = 0; i < currentOrder.length; i++) {
        if (i === currentIndex) continue;
        const otherId = currentOrder[i];
        const el = elementRefsMap.current[otherId];
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        const otherMidY = rect.top + rect.height / 2;

        if (i < currentIndex && myCenterY < otherMidY) {
          targetIndex = Math.min(targetIndex, i);
        } else if (i > currentIndex && myCenterY > otherMidY) {
          targetIndex = Math.max(targetIndex, i);
        }
      }

      setDropIndex(targetIndex !== currentIndex ? targetIndex : -1);
    };

    const handleUp = (upEvent) => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);

      const currentOrder = dragStartOrder.current;
      const currentIndex = currentOrder.indexOf(elementId);

      if (currentIndex !== -1 && onReorder) {
        const myCenterY = upEvent.clientY;
        let targetIndex = currentIndex;

        for (let i = 0; i < currentOrder.length; i++) {
          if (i === currentIndex) continue;
          const otherId = currentOrder[i];
          const el = elementRefsMap.current[otherId];
          if (!el) continue;
          const rect = el.getBoundingClientRect();
          const otherMidY = rect.top + rect.height / 2;

          if (i < currentIndex && myCenterY < otherMidY) {
            targetIndex = Math.min(targetIndex, i);
          } else if (i > currentIndex && myCenterY > otherMidY) {
            targetIndex = Math.max(targetIndex, i);
          }
        }

        if (targetIndex !== currentIndex) {
          const newOrder = [...currentOrder];
          const [removed] = newOrder.splice(currentIndex, 1);
          newOrder.splice(targetIndex, 0, removed);

          // Check if same as default
          const defaultOrder = getEffectiveContentOrder(null);
          const isDefault = newOrder.every((el, i) => el === defaultOrder[i]);
          onReorder(isDefault ? undefined : newOrder);
        }
      }

      setDraggingId(null);
      setDropIndex(-1);
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
  }, [data.contentOrder, onReorder, scale]);

  // Ref registration helper
  const setElementRef = useCallback((elementId, node) => {
    if (node) {
      elementRefsMap.current[elementId] = node;
    }
  }, []);

  // ── Grip handle component ──
  const GripHandle = ({ elementId }) => (
    <div
      className="reorder-grip-handle"
      onMouseDown={(e) => handleGripDown(elementId, e)}
    >
      <svg width="8" height="14" viewBox="0 0 8 14" fill="currentColor">
        <circle cx="2" cy="2" r="1.3" />
        <circle cx="6" cy="2" r="1.3" />
        <circle cx="2" cy="7" r="1.3" />
        <circle cx="6" cy="7" r="1.3" />
        <circle cx="2" cy="12" r="1.3" />
        <circle cx="6" cy="12" r="1.3" />
      </svg>
    </div>
  );

  // ── Element wrapper with hover + grip ──
  const ReorderWrap = ({ elementId, children }) => {
    const [hovered, setHovered] = useState(false);
    const isBeingDragged = draggingId === elementId;
    const showGrip = interactive && onReorder && !editingField && (hovered || isBeingDragged);

    const contentOrder = getEffectiveContentOrder(data.contentOrder);
    const myIndex = contentOrder.indexOf(elementId);
    const isDropTarget = dropIndex === myIndex && draggingId && draggingId !== elementId;

    return (
      <div
        ref={(node) => setElementRef(elementId, node)}
        data-reorder-id={elementId}
        className={`reorder-element${isBeingDragged ? ' reorder-element-dragging' : ''}${isDropTarget ? ' reorder-drop-above' : ''}`}
        onMouseEnter={() => !draggingId && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {showGrip && <GripHandle elementId={elementId} />}
        {children}
      </div>
    );
  };

  // ── Element renderers ──
  const renderElement = (elementId) => {
    let content = null;

    switch (elementId) {
      case 'subtitle':
        content = (data.subtitleEnabled !== false && data.subtitle) || editingField === 'subtitle' ? (
          <InlineEditable
            value={data.subtitle || ''}
            isEditing={editingField === 'subtitle'}
            onChange={(text) => onEditChange && onEditChange('subtitle', text)}
            onBlur={onEndEdit}
            plainText
            className={cn('mt-5 text-[28px] leading-snug whitespace-pre-line', editingField === 'subtitle' ? 'preview-editing' : (interactive && 'preview-clickable'))}
            style={{ fontFamily: fontPreset.subtitle, color: pc.el.subtitle }}
            onClick={handleClick('text', 'subtitle')}
          >
            {data.subtitle}
          </InlineEditable>
        ) : null;
        break;

      case 'body':
        content = (data.bodyEnabled !== false && data.body) || editingField === 'body' ? (
          <InlineEditable
            value={data.body || ''}
            isEditing={editingField === 'body'}
            onChange={(html) => onEditChange && onEditChange('body', html)}
            onBlur={onEndEdit}
            className={cn('mt-6 leading-relaxed whitespace-pre-line', bodyClass, editingField === 'body' ? 'preview-editing' : (interactive && 'preview-clickable'))}
            style={{ fontFamily: fontPreset.body, color: pc.el.body, textAlign: data.bodyAlign || 'left', fontSize: `${BODY_FONT_SIZE_LEVELS[data.bodyFontSize ?? DEFAULT_BODY_FONT_SIZE]?.size || 32}px` }}
            onClick={handleClick('text', 'body')}
          >
            {renderFormattedText(data.body)}
          </InlineEditable>
        ) : null;
        break;

      case 'imageWidget':
        content = data.imageWidget ? (
          <div className={interactive ? 'preview-clickable' : undefined} onClick={handleClick('blocks', 'imageWidget')}>
            <ImageWidget
              label={data.imageWidget.label}
              caption={data.imageWidget.caption}
              src={data.imageWidget.src}
              aspectRatio={data.imageWidget.aspectRatio}
              size={data.imageWidget.size}
              blur={data.widgetBlur}
              opacity={data.widgetOpacity}
              showBorder={data.widgetBorder !== false}
              fontPreset={fontPreset}
              expandVertical={data.imageWidget.expandVertical}
              pc={pc}
            />
          </div>
        ) : null;
        break;

      case 'videoWidget':
        content = data.videoWidget ? (
          <div className={interactive ? 'preview-clickable' : undefined} onClick={handleClick('blocks', 'videoWidget')}>
            <VideoWidget
              label={data.videoWidget.label}
              caption={data.videoWidget.caption}
              src={data.videoWidget.src}
              aspectRatio={data.videoWidget.aspectRatio}
              size={data.videoWidget.size}
              blur={data.widgetBlur}
              opacity={data.widgetOpacity}
              showBorder={data.widgetBorder !== false}
              fontPreset={fontPreset}
              expandVertical={data.videoWidget.expandVertical}
              pc={pc}
            />
          </div>
        ) : null;
        break;

      case 'callout':
        content = data.callout ? (
          <div className={interactive ? 'preview-clickable' : undefined} onClick={handleClick('blocks', 'callout')}>
            <Callout
              label={data.callout.label}
              text={data.callout.text}
              blur={data.widgetBlur}
              opacity={data.widgetOpacity}
              showBorder={data.widgetBorder !== false}
              pc={pc}
            />
          </div>
        ) : null;
        break;

      case 'progress':
        content = data.progress ? (
          <div className={interactive ? 'preview-clickable' : undefined} onClick={handleClick('blocks', 'progress')}>
            <ProgressBar
              label={data.progress.label}
              current={data.progress.current}
              total={data.progress.total}
              blur={data.widgetBlur}
              opacity={data.widgetOpacity}
              showBorder={data.widgetBorder !== false}
              pc={pc}
            />
          </div>
        ) : null;
        break;

      case 'stats':
        content = data.stats?.length ? (
          <div className={cn('mt-6 grid grid-cols-3 gap-3', interactive && 'preview-clickable')} onClick={handleClick('blocks', 'stats')}>
            {data.stats.map((st, i) => (
              <MiniStat
                key={i}
                label={st.label}
                value={st.value}
                blur={data.widgetBlur}
                opacity={data.widgetOpacity}
                showBorder={data.widgetBorder !== false}
                pc={pc}
              />
            ))}
          </div>
        ) : null;
        break;

      case 'paragraphs':
        content = data.paragraphs?.length ? (
          <div className={cn('mt-6 space-y-4', interactive && 'preview-clickable')} style={{ fontFamily: fontPreset.body }} onClick={handleClick('text', 'body')}>
            {data.paragraphs.map((p, i) => (
              <p key={i} className="text-[32px] leading-relaxed" style={{ color: pc.el.paragraph }}>
                {renderFormattedText(p)}
              </p>
            ))}
          </div>
        ) : null;
        break;

      case 'bullets':
        content = data.bulletsEnabled !== false && data.bullets?.length ? (
          <ul className={cn('mt-6 space-y-4', interactive && 'preview-clickable')} style={{ fontFamily: fontPreset.body }} onClick={handleClick('blocks', 'bullets')}>
            {data.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-3 text-[32px] leading-relaxed" style={{ color: pc.el.bulletText }}>
                <span className="mt-[18px] h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: pc.el.bulletDot }} />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        ) : null;
        break;

      case 'tags':
        content = data.tagsEnabled !== false && data.meta?.length ? (
          <div className={cn('mt-7 flex flex-wrap gap-3', interactive && 'preview-clickable')} onClick={handleClick('blocks', 'tags')}>
            {data.meta.map((t, i) => (
              <span
                key={i}
                className="rounded-full border px-6 py-3 text-[26px]"
                style={{
                  color: pc.el.tagText,
                  backgroundColor: pc.el.tagBg,
                  borderColor: pc.el.tagBorder,
                }}
              >
                {t}
              </span>
            ))}
          </div>
        ) : null;
        break;

      default:
        return null;
    }

    if (!content) return null;

    // In interactive mode with reorder enabled, wrap in ReorderWrap
    if (interactive && onReorder) {
      return (
        <ReorderWrap key={elementId} elementId={elementId}>
          {content}
        </ReorderWrap>
      );
    }

    return <React.Fragment key={elementId}>{content}</React.Fragment>;
  };

  // Get the effective content order for this slide
  const effectiveOrder = getEffectiveContentOrder(data.contentOrder);

  // ── Drop indicator line (shown between elements during drag) ──
  const renderDropIndicator = (beforeIndex) => {
    if (!draggingId || dropIndex === -1) return null;
    if (beforeIndex !== dropIndex) return null;
    return <div key={`drop-${beforeIndex}`} className="reorder-drop-indicator" />;
  };

  return (
    <div className="min-w-0 w-full">
      {/* ══ HEADER — Always first ══ */}
      {(showKicker && data.kickerEnabled !== false && data.kicker) || editingField === 'kicker' ? (
        <InlineEditable
          value={data.kicker || ''}
          isEditing={editingField === 'kicker'}
          onChange={(text) => onEditChange && onEditChange('kicker', text)}
          onBlur={onEndEdit}
          plainText
          className={cn('text-[28px] leading-snug whitespace-pre-line', editingField === 'kicker' ? 'preview-editing' : (interactive && 'preview-clickable'))}
          style={{ fontFamily: fontPreset.subtitle, color: pc.el.kicker }}
          onClick={handleClick('header', 'kicker')}
        >
          {data.kicker}
        </InlineEditable>
      ) : null}

      <InlineEditable
        value={data.title}
        isEditing={editingField === 'title'}
        onChange={(html) => onEditChange && onEditChange('title', html)}
        onBlur={onEndEdit}
        className={cn(
          'whitespace-pre-line leading-[1.02]',
          data.kickerEnabled !== false && data.kicker && 'mt-3',
          titleClass,
          editingField === 'title' ? 'preview-editing' : (interactive && 'preview-clickable')
        )}
        style={{
          fontSize: `${TITLE_FONT_SIZE_LEVELS[data.titleFontSize ?? DEFAULT_TITLE_FONT_SIZE]?.size || 74}px`,
          fontFamily: fontPreset.title,
          fontWeight: fontPreset.titleWeight,
          color: pc.el.title,
          textAlign: data.titleAlign || 'left',
        }}
        onClick={handleClick('header', 'title')}
      >
        {renderFormattedText(data.title)}
      </InlineEditable>

      {afterTitle}

      {/* ══ MIDDLE — Order determined by contentOrder, with reorder support ══ */}
      {effectiveOrder.map((elementId, idx) => {
        const el = renderElement(elementId);
        if (!el) return null;
        return (
          <React.Fragment key={elementId}>
            {renderDropIndicator(idx)}
            {el}
          </React.Fragment>
        );
      })}
      {/* Drop indicator at end */}
      {renderDropIndicator(effectiveOrder.length)}

      {/* ══ FOOTER — Always last ══ */}
      {beforeFooter}

      {showFooter && data.footerEnabled !== false && (
        <div className={cn('mt-10 flex w-full items-end justify-between', footerClass)}>
          <InlineEditable
            value={data.ctaButton || ''}
            isEditing={editingField === 'ctaButton'}
            onChange={(text) => onEditChange && onEditChange('ctaButton', text)}
            onBlur={onEndEdit}
            plainText
            className={editingField === 'ctaButton' ? 'preview-editing' : (interactive ? 'preview-clickable' : '')}
            style={{ fontSize: '22px', color: pc.el.footer }}
            onClick={handleClick('footer', 'ctaButton')}
          >
            {data.ctaButton ?? 'magicforest.land'}
          </InlineEditable>
          <InlineEditable
            value={data.cta || ''}
            isEditing={editingField === 'cta'}
            onChange={(text) => onEditChange && onEditChange('cta', text)}
            onBlur={onEndEdit}
            plainText
            className={cn('text-right', editingField === 'cta' ? 'preview-editing' : (interactive ? 'preview-clickable' : ''))}
            style={{ fontSize: '24px', color: pc.el.footerCta }}
            onClick={handleClick('footer', 'cta')}
          >
            {data.cta || ''}
          </InlineEditable>
        </div>
      )}
    </div>
  );
}
